import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useToggle } from '../../../hooks/useToggle';
import { LockAction } from '@g17eco/types/questionInterfaces';
import { QUESTION, SURVEY } from '@constants/terminology';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { getAssuranceStatusText } from '@utils/universalTrackerValue';
import { SurveyModelMinimalUtrv } from '@g17eco/types/survey';

interface QuestionLockModalProps {
  handleLockAction: (action: LockAction) => Promise<void>;
  isLocked: boolean;
  isDisabled: boolean;
  utrv: Pick<SurveyModelMinimalUtrv, 'assuranceStatus'>;
}

const getDisabledButtonTooltipText = (isLocked: boolean) => {
  const action = isLocked ? 'unlocked' : 'locked';
  return `Question cannot be ${action} whilst ${SURVEY.SINGULAR} is completed`;
};

const ModalMessage = ({ isLocked, utrv }: Pick<QuestionLockModalProps, 'isLocked' | 'utrv'>) => {
  if (isLocked) {
    const statusText = getAssuranceStatusText(utrv);
    return (
      <>
        You are unlocking a {QUESTION.SINGULAR} that has been {statusText?.toLowerCase()}.
        <br />
        <br />
        If you restate any of the core information, then you must include an explanation in the Further explanation
        section.
        <br />
        The {QUESTION.SINGULAR} will be marked as 'Restated', not '{statusText}'.
        <br />
        You can still view the Assured data in the {QUESTION.SINGULAR}'s Provenance section.
      </>
    );
  }

  return (
    <>
      You are locking a {QUESTION.SINGULAR} that has been assured or partially assured. This will stop contributors from
      being able to make additional changes.
      <br />
      <br />
      All data can still be viewed in the {QUESTION.SINGULAR}'s Provenance section.
    </>
  );
};

export const QuestionLockButton = ({ handleLockAction, isLocked, isDisabled, utrv }: QuestionLockModalProps) => {
  const [isOpen, toggle] = useToggle(false);

  const handleProceed = async () => {
    return handleLockAction(isLocked ? LockAction.Open : LockAction.Close).finally(() => {
      toggle();
    });
  };

  return (
    <>
      <SimpleTooltip text={isDisabled ? getDisabledButtonTooltipText(isLocked) : ''}>
        <Button color='link' onClick={toggle} disabled={isDisabled}>
          {isLocked ? `Unlock ${QUESTION.SINGULAR}` : `Lock ${QUESTION.SINGULAR}`}
        </Button>
      </SimpleTooltip>
      <Modal isOpen={isOpen} toggle={toggle} backdrop='static'>
        <ModalHeader className='text-ThemeWarningMedium' toggle={toggle}>
          <span className='text-ThemeWarningExtradark'>Restatement of data</span>
        </ModalHeader>
        <ModalBody>
          <ModalMessage isLocked={isLocked} utrv={utrv} />
        </ModalBody>
        <ModalFooter className='pt-0'>
          <Button color='transparent' onClick={toggle}>
            Cancel
          </Button>
          <SubmitButton color={'warning'} onClick={handleProceed}>
            Proceed
          </SubmitButton>
        </ModalFooter>
      </Modal>
    </>
  );
};
